// Shared helpers for deriving checklist completion counts from a task row.
//
// Background — see fix(#67):
//
// The list endpoint `/api/v1/practices/{id}/tasks/` returns a pre-computed
// `checklist_progress` string ("done/total") on each row and DOES NOT include
// the `checklist_items` array. The detail endpoint returns the items but the
// task list/table only has the summary string. The lead-table Checklist column
// previously read `row.checklist_items` and counted `is_completed === true`,
// which:
//   1. Returned `undefined.length` (rendered as "—") for every list-shape row
//      because the field wasn't on the payload.
//   2. Even when items were present (detail-shape rows), under-counted because
//      it ignored the V2 source-of-truth `completion_state === 'YES'` that the
//      detail view (TaskDetailChecklist, TaskSlideoutChecklist) treats as
//      complete.
//
// This helper makes the table cell match the detail view by:
//   - Preferring the BE-computed `checklist_progress` string (same value the
//     detail view header displays).
//   - Falling back to recomputing from `checklist_items` using the SAME
//     completion conditions the detail view applies.

/**
 * Returns `{ done, total }` for a task row's checklist completion, matching
 * what the lead detail Tasks view displays.
 *
 * Resolution order:
 *   1. `row.checklist_progress` string ("done/total") — pre-computed by the BE
 *      and used by the detail view header.
 *   2. `row.checklist_items[]` — count items where any of `is_completed`,
 *      `completed`, or `completion_state === 'YES'` is truthy (same logic as
 *      TaskDetailChecklist / TaskSlideoutChecklist).
 *
 * Returns `null` when neither source is available so the caller can render an
 * em-dash placeholder.
 *
 * @param {object} row - Task row from `/tasks/` list or detail endpoint
 * @returns {{done: number, total: number} | null}
 */
export function getChecklistProgress(row) {
    if (!row || typeof row !== 'object') return null;

    // Prefer the BE-computed string. Detail view header reads the same field.
    const progress = row.checklist_progress;
    if (typeof progress === 'string') {
        const m = progress.match(/^\s*(\d+)\s*\/\s*(\d+)\s*$/);
        if (m) {
            const done = parseInt(m[1], 10);
            const total = parseInt(m[2], 10);
            if (Number.isFinite(done) && Number.isFinite(total)) {
                return { done, total };
            }
        }
    }

    // Fallback: recompute from items if present (detail-shape rows).
    const items = row.checklist_items;
    if (Array.isArray(items) && items.length > 0) {
        const done = items.filter(
            (item) =>
                item && (item.is_completed || item.completed || item.completion_state === 'YES')
        ).length;
        return { done, total: items.length };
    }

    if (Array.isArray(items) && items.length === 0) {
        return { done: 0, total: 0 };
    }

    return null;
}
